import React, { useState, useEffect } from 'react';
import { 
  Card, Table, Tag, Button, Space, Typography, Progress, 
  Tooltip, Breadcrumb, Tabs, Empty
} from 'antd';
import { 
  ArrowLeftOutlined,
  HomeOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import OTRStatisticalVarianceChart from './OTRStatisticalVarianceChart';
import OTRTripDetailView from './OTRTripDetailView';
import { ProposedOTR } from './ProposedOTR';
import { toTime } from '../../../libs/formatLib';

const { TabPane } = Tabs;

// View level constants
const VIEW_LEVELS = {
  TRIP_AGGREGATE: 1,   // Level 1: Aggregate trip performance
  TRIP_BY_DATE: 2,     // Level 2: Trip performance by date
  TRIP_DETAIL: 3,      // Level 3: Trip detail (stop-by-stop)
  STOP_DETAIL: 4       // Level 4: Stop detail (showing trips for a specific stop)
};

const OTRTripPerformanceTab = ({ 
  tripData,
  stopData,
  filteredData,
  viewLevel,
  selectedTrip,
  selectedDate,
  selectedStop,
  tripStartTimes,
  onTripClick,
  onDateClick,
  onBack,
  reportData,
  dateFilteredDataset,
  isDateDatasetLoaded
}) => {
  

  // Columns for Level 1: Trip Aggregate View
  const tripColumns = [
    {
      title: 'Route',
      dataIndex: 'route',
      key: 'route',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => {
        const routeA = a.route;
        const routeB = b.route;
        
        // First, compare routes
        let routeComparison;
        if (typeof routeA === 'number' && typeof routeB === 'number') {
          routeComparison = routeA - routeB;
        } else {
          routeComparison = String(routeA || '').localeCompare(String(routeB || ''));
        }
        
        // If routes are the same, sort by trip start time
        if (routeComparison === 0) {
          const timeA = tripStartTimes[a.tripKey]?.time || '';
          const timeB = tripStartTimes[b.tripKey]?.time || '';
          return timeA.localeCompare(timeB);
        }
        
        return routeComparison;
      },
      width: 80,
    },
    {
      title: 'Trip Start',
      dataIndex: 'tripKey',
      key: 'tripKey',
      sorter: (a, b) => {
        const timeA = tripStartTimes[a.tripKey]?.time || '';
        const timeB = tripStartTimes[b.tripKey]?.time || '';
        return timeA.localeCompare(timeB);
      },
      render: (tripKey, record) => (
        <Button 
          type="link" 
          style={{ 
            padding: 0, 
            fontSize: '14px', 
            fontWeight: 'bold',
            textDecoration: 'underline'
          }} 
          onClick={() => onTripClick(tripKey)}
        >
          {tripStartTimes[tripKey]?.time || 'undefined'}
        </Button>
      ),
      width: 100,
    },
    {
      title: 'Trips',
      dataIndex: 'dateCount',
      key: 'dateCount',
      sorter: (a, b) => a.dateCount - b.dateCount,
      width: 60,
      render: (count) => (
        <Tooltip title="Number of days this trip was operated">
          <span>{count}</span>
        </Tooltip>
      ),
    },
    {
      title: 'First OTR %',
      dataIndex: ['firstStopStats', 'onTimePercent'],
      key: 'firstStopOTR',
      render: (val, record) => {
        const value = val || 0;
        let color = 'green';
        if (value < 70) color = 'blue';
        else if (value < 90) color = 'red';
        
        return (
          <Tooltip title={
            <div>
              <div>On Time: {Math.round(value)}%</div>
              <div>Early: {Math.round(record.firstStopStats?.earlyPercent || 0)}%</div>
              <div>Late: {Math.round(record.firstStopStats?.latePercent || 0)}%</div>
              <div>Total Stops: {record.firstStopStats?.totalStops || 0}</div>
            </div>
          }>
            <Progress 
              percent={Math.round(value)} 
              size="small" 
              strokeColor={color}
              format={percent => `${percent}%`}
            />
          </Tooltip>
        );
      },
      sorter: (a, b) => (a.firstStopStats?.onTimePercent || 0) - (b.firstStopStats?.onTimePercent || 0),
      width: 120,
    },
    {
      title: 'Mid OTR %',
      key: 'midStopOTR',
      dataIndex: ['midStopStats', 'onTimePercent'],
      render: (val, record) => {
        const value = val || 0;
        let color = 'green';
        if (value < 70) color = 'blue';
        else if (value < 90) color = 'red';
        
        return (
          <Tooltip title={
            <div>
              <div>On Time: {Math.round(value)}%</div>
              <div>Early: {Math.round(record.midStopStats?.earlyPercent || 0)}%</div>
              <div>Late: {Math.round(record.midStopStats?.latePercent || 0)}%</div>
              <div>Total Stops: {record.midStopStats?.totalStops || 0}</div>
            </div>
          }>
            <Progress 
              percent={Math.round(value)} 
              size="small" 
              strokeColor={color}
              format={percent => `${percent}%`}
            />
          </Tooltip>
        );
      },
      sorter: (a, b) => (a.midStopStats?.onTimePercent || 0) - (b.midStopStats?.onTimePercent || 0),
      width: 120,
    },
    {
      title: 'Last OTR %',
      key: 'lastStopOTR',
      dataIndex: ['lastStopStats', 'onTimePercent'],
      render: (val, record) => {
        const value = val || 0;
        let color = 'green';
        if (value < 70) color = 'blue';
        else if (value < 90) color = 'red';
        
        return (
          <Tooltip title={
            <div>
              <div>On Time: {Math.round(value)}%</div>
              <div>Early: {Math.round(record.lastStopStats?.earlyPercent || 0)}%</div>
              <div>Late: {Math.round(record.lastStopStats?.latePercent || 0)}%</div>
              <div>Total Stops: {record.lastStopStats?.totalStops || 0}</div>
            </div>
          }>
            <Progress 
              percent={Math.round(value)} 
              size="small" 
              strokeColor={color}
              format={percent => `${percent}%`}
            />
          </Tooltip>
        );
      },
      sorter: (a, b) => (a.lastStopStats?.onTimePercent || 0) - (b.lastStopStats?.onTimePercent || 0),
      width: 120,
    },
    {
      title: 'Overall OTR %',
      key: 'overallOTR',
      dataIndex: ['overallStats', 'onTimePercent'],
      render: (val, record) => {
        const value = val || 0;
        let color = 'green';
        if (value < 70) color = 'blue';
        else if (value < 90) color = 'red';
        
        return (
          <Tooltip title={
            <div>
              <div>On Time: {Math.round(value)}%</div>
              <div>Early: {Math.round(record.overallStats?.earlyPercent || 0)}%</div>
              <div>Late: {Math.round(record.overallStats?.latePercent || 0)}%</div>
              <div>Total Stops: {record.overallStats?.totalStops || 0}</div>
            </div>
          }>
            <Progress 
              percent={Math.round(value)} 
              size="small" 
              strokeColor={color}
              format={percent => `${percent}%`}
            />
          </Tooltip>
        );
      },
      sorter: (a, b) => (a.overallStats?.onTimePercent || 0) - (b.overallStats?.onTimePercent || 0),
      width: 120,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        const value = record.overallStats?.onTimePercent || 0;
        let color = 'green';
        let text = 'Good';
        
        if (value < 70) {
          color = 'blue';
          text = 'Poor';
        } else if (value < 90) {
          color = 'orange';
          text = 'Fair';
        }
        
        return <Tag color={color}>{text}</Tag>;
      },
      width: 80,
    }
  ];

  // Columns for Level 2: Trip by Date View
  const tripDateColumns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      sorter: (a, b) => String(a.dateId || '').localeCompare(String(b.dateId || '')),
      render: (date, record) => (
        <Button 
          type="link" 
          onClick={() => {
            onDateClick(record.tripKey, record.dateId)
          }}
          style={{ padding: 0 }}
        >
          {date}
        </Button>
      ),
      width: 100,
    },
    {
      title: 'Route',
      dataIndex: 'route',
      key: 'route',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => {
        const routeA = a.route;
        const routeB = b.route;
        
        // First, compare routes
        let routeComparison;
        if (typeof routeA === 'number' && typeof routeB === 'number') {
          routeComparison = routeA - routeB;
        } else {
          routeComparison = String(routeA || '').localeCompare(String(routeB || ''));
        }
        
        // If routes are the same, sort by trip start time
        if (routeComparison === 0) {
          const timeA = tripStartTimes[a.tripKey]?.time || '';
          const timeB = tripStartTimes[b.tripKey]?.time || '';
          return timeA.localeCompare(timeB);
        }
        
        return routeComparison;
      },
      width: 80,
    },
    {
      title: 'Stop Count',
      dataIndex: 'stopCount',
      key: 'stopCount',
      sorter: (a, b) => a.stopCount - b.stopCount,
      width: 80,
      render: (count) => (
        <Tooltip title="Number of stops in this trip">
          <span>{count}</span>
        </Tooltip>
      ),
    },
    {
      title: 'First Stop OTR',
      dataIndex: 'firstStopStats',
      key: 'firstStopOTR',
      render: stats => {
        if (!stats || !stats.earlyCount && !stats.onTimeCount && !stats.lateCount) {
          return <Tag color="default">Unknown</Tag>;
        }

        const { earlyCount = 0, onTimeCount = 0, lateCount = 0 } = stats;
        const total = earlyCount + onTimeCount + lateCount;
        const onTimePercent = (onTimeCount / total) * 100;
        
        let color = 'green';
        if (onTimePercent < 70) color = 'blue';
        else if (onTimePercent < 90) color = 'orange';
        
        return (
          <Tooltip title={
            <div>
              <div>On Time: {Math.round(onTimePercent)}%</div>
              <div>Early: {Math.round((earlyCount / total) * 100)}%</div>
              <div>Late: {Math.round((lateCount / total) * 100)}%</div>
              <div>Total Stops: {total}</div>
            </div>
          }>
            <Tag color={color}>{onTimePercent >= 90 ? 'Good' : onTimePercent >= 70 ? 'Fair' : 'Poor'}</Tag>
          </Tooltip>
        );
      },
      width: 100,
    },
    {
      title: 'Mid Stop OTR',
      dataIndex: 'midStopStats',
      key: 'midStopOTR',
      render: stats => {
        if (!stats || !stats.earlyCount && !stats.onTimeCount && !stats.lateCount) {
          return <Tag color="default">Unknown</Tag>;
        }

        const { earlyCount = 0, onTimeCount = 0, lateCount = 0 } = stats;
        const total = earlyCount + onTimeCount + lateCount;
        const onTimePercent = (onTimeCount / total) * 100;
        
        let color = 'green';
        if (onTimePercent < 70) color = 'blue';
        else if (onTimePercent < 90) color = 'orange';
        
        return (
          <Tooltip title={
            <div>
              <div>On Time: {Math.round(onTimePercent)}%</div>
              <div>Early: {Math.round((earlyCount / total) * 100)}%</div>
              <div>Late: {Math.round((lateCount / total) * 100)}%</div>
              <div>Total Stops: {total}</div>
            </div>
          }>
            <Tag color={color}>{onTimePercent >= 90 ? 'Good' : onTimePercent >= 70 ? 'Fair' : 'Poor'}</Tag>
          </Tooltip>
        );
      },
      width: 100,
    },
    {
      title: 'Last Stop OTR',
      dataIndex: 'lastStopStats',
      key: 'lastStopOTR',
      render: stats => {
        if (!stats || !stats.earlyCount && !stats.onTimeCount && !stats.lateCount) {
          return <Tag color="default">Unknown</Tag>;
        }

        const { earlyCount = 0, onTimeCount = 0, lateCount = 0 } = stats;
        const total = earlyCount + onTimeCount + lateCount;
        const onTimePercent = (onTimeCount / total) * 100;
        
        let color = 'green';
        if (onTimePercent < 70) color = 'blue';
        else if (onTimePercent < 90) color = 'orange';
        
        return (
          <Tooltip title={
            <div>
              <div>On Time: {Math.round(onTimePercent)}%</div>
              <div>Early: {Math.round((earlyCount / total) * 100)}%</div>
              <div>Late: {Math.round((lateCount / total) * 100)}%</div>
              <div>Total Stops: {total}</div>
            </div>
          }>
            <Tag color={color}>{onTimePercent >= 90 ? 'Good' : onTimePercent >= 70 ? 'Fair' : 'Poor'}</Tag>
          </Tooltip>
        );
      },
      width: 100,
    },
    {
      title: 'Overall OTR',
      dataIndex: 'overallStats',
      key: 'overallOTR',
      render: stats => {
        if (!stats || !stats.earlyCount && !stats.onTimeCount && !stats.lateCount) {
          return <Tag color="default">Unknown</Tag>;
        }

        const { earlyCount = 0, onTimeCount = 0, lateCount = 0 } = stats;
        const total = earlyCount + onTimeCount + lateCount;
        const onTimePercent = (onTimeCount / total) * 100;
        
        let color = 'green';
        if (onTimePercent < 70) color = 'blue';
        else if (onTimePercent < 90) color = 'orange';
        
        return (
          <Tooltip title={
            <div>
              <div>On Time: {Math.round(onTimePercent)}%</div>
              <div>Early: {Math.round((earlyCount / total) * 100)}%</div>
              <div>Late: {Math.round((lateCount / total) * 100)}%</div>
              <div>Total Stops: {total}</div>
            </div>
          }>
            <Tag color={color}>{onTimePercent >= 90 ? 'Good' : onTimePercent >= 70 ? 'Fair' : 'Poor'}</Tag>
          </Tooltip>
        );
      },
      width: 100,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        const stats = record.overallStats;
        if (!stats) return <Tag color="default">Unknown</Tag>;
        
        const { earlyCount = 0, onTimeCount = 0, lateCount = 0 } = stats;
        const total = earlyCount + onTimeCount + lateCount;
        const onTimePercent = (onTimeCount / total) * 100;
        
        let color = 'green';
        let text = 'Good';
        
        if (onTimePercent < 70) {
          color = 'blue';
          text = 'Poor';
        } else if (onTimePercent < 90) {
          color = 'orange';
          text = 'Fair';
        }
        
        return <Tag color={color}>{text}</Tag>;
      },
      width: 80,
    }
  ];

  // Render navigation breadcrumb
  const renderBreadcrumb = () => {
    return (
      <Breadcrumb style={{ marginBottom: '16px' }}>
        <Breadcrumb.Item>
          <a onClick={() => onBack && onBack()}>
            <HomeOutlined /> Trips
          </a>
        </Breadcrumb.Item>
        
        {viewLevel >= VIEW_LEVELS.TRIP_BY_DATE && selectedTrip && (
          <Breadcrumb.Item>
            <a onClick={() => onBack && onBack()}>
                {selectedTrip.route} Trip {tripStartTimes[selectedTrip.tripKey]?.time + ' (' + selectedTrip.tripId + ')'}
            </a>
          </Breadcrumb.Item>
        )}
        
        {viewLevel === VIEW_LEVELS.TRIP_DETAIL && selectedDate && (
          <Breadcrumb.Item>
            Date {dayjs(String(selectedDate)).format('YYYY-MM-DD')}
          </Breadcrumb.Item>
        )}

        {viewLevel === VIEW_LEVELS.STOP_DETAIL && selectedStop && (
          <Breadcrumb.Item>
            Stop {selectedStop.TSN} {selectedStop.STOP_NAME && `(${selectedStop.STOP_NAME})`}
          </Breadcrumb.Item>
        )}
      </Breadcrumb>
    );
  };

  // Level 3: Trip Detail View (stop-by-stop)
  if (viewLevel === VIEW_LEVELS.TRIP_DETAIL && selectedTrip && selectedDate) {
    return (
      <OTRTripDetailView
        route={selectedTrip.route}
        tripId={selectedTrip.tripKey}
        dateId={selectedDate}
        onClose={onBack}
        tripStartTimes={tripStartTimes}
        preloadedData={stopData}
      />
    );
  }

  // Level 4: Stop Detail View (trips for a specific stop)
  if (viewLevel === VIEW_LEVELS.STOP_DETAIL && selectedStop) {
    // Create columns specifically for the stop detail view
    const stopDetailColumns = [
      ...tripColumns.slice(0, 2), // Take Trip ID and Route columns
      {
        title: 'Stop Type in Trip',
        dataIndex: 'stopType',
        key: 'stopType',
        render: stopType => {
          let color = 'default';
          if (stopType === 'First') color = 'green';
          if (stopType === 'Mid') color = 'blue';
          if (stopType === 'Last') color = 'orange';
          
          return <Tag color={color}>{stopType}</Tag>;
        },
        filters: [
          { text: 'First', value: 'First' },
          { text: 'Mid', value: 'Mid' },
          { text: 'Last', value: 'Last' },
          { text: 'Unknown', value: 'Unknown' }
        ],
        onFilter: (value, record) => record.stopType === value,
      },
      ...tripColumns.slice(2) // Add the remaining columns
    ];
    
    return (
      <>
        {renderBreadcrumb()}
        <Card
          title={
            <Space>
              <Button
                type="link"
                icon={<ArrowLeftOutlined />}
                onClick={onBack}
              >
                Back to Stop Level
              </Button>
              {`Trips for Stop ${selectedStop.TSN} ${selectedStop.STOP_NAME ? `(${selectedStop.STOP_NAME})` : ''}`}
            </Space>
          }
        >
          <Table
            columns={stopDetailColumns}
            dataSource={filteredData}
            rowKey="tripId"
            pagination={{ pageSize: 20 }}
          />
        </Card>
      </>
    );
  }

  return (
    <>
      {/* Add Multi-Trip Chart for Level 2 */}
      {viewLevel === VIEW_LEVELS.TRIP_BY_DATE && filteredData.length > 0 && selectedTrip && (
        <div style={{ marginBottom: '20px' }}>
          <Card title={<Space>
            <Button
              type="link"
              icon={<ArrowLeftOutlined />}
              onClick={onBack}
            >
              Back
            </Button>Trip Analysis</Space>}>
            <Tabs defaultActiveKey="variance">
              <TabPane tab="Trip Variance by Stop" key="variance">
                <OTRStatisticalVarianceChart 
                  selectedRoute={selectedTrip?.route}
                  selectedTrip={selectedTrip}
                  title="Trip Variance by Stop"
                  filteredData={stopData}
                />
              </TabPane>
              <TabPane tab="Proposed Timetable" key="proposed">
                <Card title="Proposed Timetable">
                                  <ProposedOTR
                  reportData={reportData}
                  selectedRoute={selectedTrip?.route}
                  selectedTrip={selectedTrip?.tripKey}
                  selectedTripStart={selectedTrip ? tripStartTimes[selectedTrip.tripKey]?.time : null}
                  setSelectedRoute={null}
                  setSelectedTrip={null}
                  dateRange={[dayjs().subtract(30, 'day'), dayjs()]}
                  initialShowStopTimes={true}
                  initialShowScheduledTime={true}
                  initialShowProposedTime={true}
                  initialShowModelLines={false}
                  filteredData={stopData}
                />
                </Card>
              </TabPane>
            </Tabs>
          </Card>
        </div>
      )}
      
      {viewLevel > VIEW_LEVELS.TRIP_AGGREGATE && renderBreadcrumb()}
      
      <Card
        title={
          <Space>
            {viewLevel > VIEW_LEVELS.TRIP_AGGREGATE && (
              <Button
                type="link"
                icon={<ArrowLeftOutlined />}
                onClick={onBack}
              >
                Back
              </Button>
            )}
            {viewLevel === VIEW_LEVELS.TRIP_AGGREGATE && 'Trip Performance Summary'}
            {viewLevel === VIEW_LEVELS.TRIP_BY_DATE && selectedTrip && `${selectedTrip.route} ${tripStartTimes[selectedTrip.tripKey]?.time || selectedTrip.tripId} Performance by Date`}
          </Space>
        }
      >
        <Table
          columns={viewLevel === VIEW_LEVELS.TRIP_AGGREGATE ? tripColumns : tripDateColumns}
          dataSource={filteredData}
          rowKey={viewLevel === VIEW_LEVELS.TRIP_AGGREGATE ? 'tripId' : record => `${record.tripId}-${record.dateId}`}
          pagination={{ pageSize: 20 }}
        />
      </Card>
    </>
  );
};

export default OTRTripPerformanceTab; 